/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { createReducer, on } from '@ngrx/store';
import { GetSegmentInfoResponseDto } from 'customer-speed-api';
import moment from 'moment';
import { ILatLng } from '../../../core/models';
import { IRoadDetails, ISelectedSegment } from '../../models';
import { EManagePostedSpeedTabs } from '../../models/manage-posted-speed-tabs.enum';
import { ROAD_DIRECTION, RoadDirection } from '../../models/road-details.interface';
import { evaluateContiguity } from '../../services/contiguity-engine';
import { decodeGeometry } from '../../utils/decode-geometry';
import * as ManagePostedSpeedActions from '../actions/manage-posted-speed.actions';
import { IManagePostedSpeedState, ISegmentSelectionSnapshot, initialManagePostedSpeedState } from '../manage-posted-speed.state';

/**
 * Derives the backward-compatible `selectedRoads` array from computed segment groups.
 * Each group produces one `IRoadDetails` entry from the group's merged road details.
 *
 * @param groups - Computed segment groups from the contiguity engine
 * @returns Array of road details, one per group
 */
function deriveSelectedRoads(groups: IManagePostedSpeedState['segmentGroups']): IRoadDetails[] {
  return groups.map(group => group.mergedRoadDetails);
}

// Re-export for backward compatibility with existing test imports
export { decodeGeometry } from '../../utils/decode-geometry';

/**
 * Builds an {@link ISelectedSegment} from the API response payload.
 */
function buildSelectedSegment(segmentInfo: GetSegmentInfoResponseDto, roadDetails: IRoadDetails, decoded: ILatLng[]): ISelectedSegment {
  return {
    segmentId: segmentInfo.segmentId ?? '',
    segmentRef: segmentInfo.segmentRef ?? '',
    roadName: roadDetails.roadName ?? roadDetails.fullAddress ?? '',
    geometry: decoded,
    roadDetails,
    segmentInfo
  };
}

/**
 * Creates history state fields by snapshotting the current selectedSegments
 * onto the past stack and clearing the future stack.
 *
 * This is a pure function used by segment mutation action handlers to record
 * the pre-mutation state before applying changes.
 *
 * @param state - Current manage posted speed state
 * @returns Partial state with updated past, future, canUndo, canRedo
 */
export function pushToHistory(state: IManagePostedSpeedState): Pick<IManagePostedSpeedState, 'past' | 'future' | 'canUndo' | 'canRedo'> {
  const snapshot: ISegmentSelectionSnapshot = {
    selectedSegments: state.selectedSegments
  };
  return {
    past: [...state.past, snapshot],
    future: [],
    canUndo: true,
    canRedo: false
  };
}

/**
 * Restores a segment selection snapshot and recomputes derived state.
 * Shared between undo and redo handlers to avoid duplication.
 */
function restoreSnapshot(
  state: IManagePostedSpeedState,
  snapshot: ISegmentSelectionSnapshot,
  newPast: ISegmentSelectionSnapshot[],
  newFuture: ISegmentSelectionSnapshot[]
): IManagePostedSpeedState {
  const restoredSegments = snapshot.selectedSegments;
  const segmentGroups = evaluateContiguity(restoredSegments);
  const selectedRoads = deriveSelectedRoads(segmentGroups);

  return {
    ...state,
    selectedSegments: restoredSegments,
    segmentGroups,
    selectedRoads,
    isSegmentSelected: Object.keys(restoredSegments).length > 0,
    past: newPast,
    future: newFuture,
    canUndo: newPast.length > 0,
    canRedo: newFuture.length > 0
  };
}

const reducer = createReducer(
  initialManagePostedSpeedState,

  // Panel Management
  on(ManagePostedSpeedActions.openManagePostedSpeedPanel, (_state, { mapCenter }) => ({
    ...initialManagePostedSpeedState,
    mapCenter
  })),

  on(ManagePostedSpeedActions.closeManagePostedSpeedPanel, () => ({
    ...initialManagePostedSpeedState
  })),

  // Tab Management
  on(ManagePostedSpeedActions.setActiveTab, (state, { tab }) => ({
    ...state,
    activeTab: state.isUpdateFormOpen ? state.activeTab : tab,
    ...(tab !== EManagePostedSpeedTabs.EditDelete && {
      hoveredUpdateId: null,
      selectedUpdateId: null
    })
  })),

  // Load Overrides
  on(ManagePostedSpeedActions.loadOverridesSuccess, (state, { overrides, totalItems, totalAccountOverrides }) => ({
    ...state,
    postedSpeedUpdates: overrides,
    totalItems,
    totalAccountOverrides
  })),

  on(
    ManagePostedSpeedActions.loadOverridesFailure,
    (state): IManagePostedSpeedState => ({
      ...state,
      postedSpeedUpdates: []
    })
  ),

  // Filters
  on(ManagePostedSpeedActions.applyFilters, (state, { filters }) => ({
    ...state,
    filters
  })),

  on(
    ManagePostedSpeedActions.clearFilters,
    (state): IManagePostedSpeedState => ({
      ...state,
      filters: { ...initialManagePostedSpeedState.filters }
    })
  ),

  on(ManagePostedSpeedActions.setAllowMapRefine, (state, { allowMapRefine }) => ({
    ...state,
    allowMapRefine
  })),

  // Posted Speed Updates
  on(ManagePostedSpeedActions.updatePostedSpeed, state => {
    const roads = state.selectedRoads;
    const directions = new Set(roads.map(r => r.direction));
    const resolvedDirection = directions.size === 1 ? [...directions][0] : ROAD_DIRECTION.Both;
    const todayLocal = moment().format('YYYY-MM-DD');

    return {
      ...state,
      isUpdateFormOpen: true,
      updateFormSelectedRoads: roads,
      activeTab: EManagePostedSpeedTabs.Update,
      updateSpeedLimitForm: {
        ...initialManagePostedSpeedState.updateSpeedLimitForm,
        directionOfEdit: resolvedDirection,
        startDate: todayLocal
      }
    };
  }),

  on(ManagePostedSpeedActions.editPostedSpeedUpdate, (state, { update }) => {
    const formValues = {
      ...initialManagePostedSpeedState.updateSpeedLimitForm,
      directionOfEdit: update.direction.toLowerCase() as RoadDirection,
      startDate: update.startDate ? moment(update.startDate).format('YYYY-MM-DD') : null,
      endDate: update.endDate ? moment(update.endDate).format('YYYY-MM-DD') : null,
      notes: update.note || ''
    };
    // Use the original segment direction (physical road characteristic) for the road details card.
    // This determines whether the direction dropdown is disabled (one-way roads lock direction).
    const roadDirection = (update.segmentDirection || update.direction).toLowerCase() as RoadDirection;
    return {
      ...state,
      isUpdateFormOpen: true,
      activeTab: EManagePostedSpeedTabs.EditDelete,
      editingOverrideId: update.id,
      updateFormSelectedRoads: [
        {
          fullAddress: update.roadName,
          roadName: update.roadName,
          cityName: update.cityName,
          speedLimitInKmh: update.speedOverwriteInKmh,
          direction: roadDirection,
          distanceKm: update.distanceKm
        }
      ],
      updateSpeedLimitForm: formValues,
      originalFormValues: formValues
    };
  }),

  on(
    ManagePostedSpeedActions.closeUpdateSpeedLimitForm,
    (state): IManagePostedSpeedState => ({
      ...state,
      isUpdateFormOpen: false,
      editingOverrideId: null,
      originalFormValues: null
    })
  ),

  // Save Override
  on(
    ManagePostedSpeedActions.saveOverride,
    (state): IManagePostedSpeedState => ({
      ...state,
      isSaving: true
    })
  ),

  on(
    ManagePostedSpeedActions.saveOverrideSuccess,
    (state): IManagePostedSpeedState => ({
      ...state,
      isSaving: false,
      isUpdateFormOpen: false,
      lastSavedOverrideId: state.editingOverrideId,
      editingOverrideId: null,
      originalFormValues: null,
      activeTab: EManagePostedSpeedTabs.EditDelete,
      updateSpeedLimitForm: { ...initialManagePostedSpeedState.updateSpeedLimitForm },
      selectedSegments: {},
      segmentGroups: [],
      selectedRoads: [],
      isSegmentSelected: false,
      selectedSegmentId: null,
      selectedSegmentRef: null,
      decodedGeometry: null,
      segmentInfo: null,
      past: [] as ISegmentSelectionSnapshot[],
      future: [] as ISegmentSelectionSnapshot[],
      canUndo: false,
      canRedo: false
    })
  ),

  on(
    ManagePostedSpeedActions.saveOverrideFailure,
    (state): IManagePostedSpeedState => ({
      ...state,
      isSaving: false
    })
  ),

  on(ManagePostedSpeedActions.updateSpeedLimitFormField, (state, { field }) => {
    const updatedForm = { ...state.updateSpeedLimitForm, ...field };
    // Capture the speed value into originalFormValues if this is the initial population during edit flow
    const shouldUpdateOriginal =
      state.originalFormValues != null && state.originalFormValues.updatedPostedSpeedLimit == null && field.updatedPostedSpeedLimit != null;
    return {
      ...state,
      updateSpeedLimitForm: updatedForm,
      originalFormValues: shouldUpdateOriginal
        ? { ...state.originalFormValues, updatedPostedSpeedLimit: field.updatedPostedSpeedLimit }
        : state.originalFormValues
    };
  }),

  on(ManagePostedSpeedActions.clearSpeedLimitForm, state => ({
    ...state,
    updateSpeedLimitForm: state.originalFormValues
      ? { ...state.originalFormValues }
      : { ...initialManagePostedSpeedState.updateSpeedLimitForm }
  })),

  // Delete Operations
  on(ManagePostedSpeedActions.deletePostedSpeedUpdateSuccess, (state, { updateId }) => ({
    ...state,
    postedSpeedUpdates: state.postedSpeedUpdates.filter(update => update.id !== updateId)
  })),

  // Drawer State Management
  on(ManagePostedSpeedActions.updateDrawerState, (state, { state: drawerState }) => ({
    ...state,
    drawerState
  })),

  // Segment Info
  on(ManagePostedSpeedActions.getSegmentInfoSuccess, (state, { roadDetails, segmentInfo }) => {
    const decoded = segmentInfo?.geometry ? decodeGeometry(segmentInfo.geometry) : null;
    const isValid = decoded !== null && decoded.length >= 2;
    const segmentId = segmentInfo?.segmentId ?? null;

    // For map rendering: reverse geometry for backward segments so A→B follows travel direction
    const renderGeometry = isValid && decoded && roadDetails.direction === ROAD_DIRECTION.Backward ? [...decoded].reverse() : decoded;

    // Build selectedSegments using ORIGINAL geometry (for contiguity endpoint checks)
    let selectedSegments: Record<string, ISelectedSegment> = {};
    let segmentGroups: IManagePostedSpeedState['segmentGroups'] = [];

    // Only push to history if this action actually modifies selectedSegments
    const willModifySelection = isValid && segmentId && segmentInfo;
    const historyUpdate = willModifySelection ? pushToHistory(state) : {};

    if (willModifySelection) {
      const segment = buildSelectedSegment(segmentInfo, roadDetails, decoded);
      selectedSegments = { [segmentId]: segment };
      segmentGroups = evaluateContiguity(selectedSegments);
    }

    return {
      ...state,
      ...historyUpdate,
      selectedRoads: [roadDetails],
      selectedSegmentId: segmentId,
      selectedSegmentRef: segmentInfo?.segmentRef ?? null,
      decodedGeometry: renderGeometry,
      segmentInfo: segmentInfo ?? null,
      isSegmentSelected: isValid,
      selectedSegments,
      segmentGroups,
      segmentError: false
    };
  }),

  on(ManagePostedSpeedActions.clearSegmentSelection, (state): IManagePostedSpeedState => {
    const historyUpdate = pushToHistory(state);
    return {
      ...state,
      ...historyUpdate,
      selectedSegmentId: null,
      selectedSegmentRef: null,
      decodedGeometry: null,
      segmentInfo: null,
      isSegmentSelected: false,
      selectedSegments: {},
      segmentGroups: [],
      selectedRoads: [],
      segmentError: false
    };
  }),

  on(
    ManagePostedSpeedActions.getSegmentInfoFailure,
    (state, { roadNotAvailable }): IManagePostedSpeedState => ({
      ...state,
      segmentError: !roadNotAvailable,
      isSegmentSelected: false
    })
  ),

  on(
    ManagePostedSpeedActions.addSegmentToSelectionFailure,
    (state, { roadNotAvailable }): IManagePostedSpeedState => ({
      ...state,
      segmentError: !roadNotAvailable
    })
  ),

  // Card Hover & Selection
  on(ManagePostedSpeedActions.hoverUpdateCard, (state, { updateId }) => ({
    ...state,
    hoveredUpdateId: updateId
  })),

  on(ManagePostedSpeedActions.selectUpdateCard, (state, { updateId }) => ({
    ...state,
    selectedUpdateId: updateId,
    lastSavedOverrideId: null
  })),

  // Multi-Segment Selection
  on(ManagePostedSpeedActions.addSegmentToSelection, (state, { segmentInfo, roadDetails }) => {
    const segmentId = segmentInfo.segmentId ?? '';
    const decoded = segmentInfo.geometry ? decodeGeometry(segmentInfo.geometry) : [];

    // Validate: reject segments with empty segmentId (Requirement 11.2)
    if (!segmentId) {
      return state;
    }

    // Validate: reject segments with fewer than 2 geometry points (Requirement 11.1)
    if (decoded.length < 2) {
      return state;
    }

    const historyUpdate = pushToHistory(state);

    // Toggle: if segment already selected, remove it (Requirement 4.3)
    if (state.selectedSegments[segmentId]) {
      const { [segmentId]: _removed, ...remaining } = state.selectedSegments;
      const toggledGroups = evaluateContiguity(remaining);
      const toggledRoads = deriveSelectedRoads(toggledGroups);
      const hasSegments = Object.keys(remaining).length > 0;

      return {
        ...state,
        ...historyUpdate,
        selectedSegments: remaining,
        segmentGroups: toggledGroups,
        selectedRoads: toggledRoads,
        isSegmentSelected: hasSegments,
        segmentError: false
      };
    }

    // Direction constraint is checked in the effect layer before dispatching addSegmentToSelection.
    // Build ISelectedSegment from payload
    const newSegment = buildSelectedSegment(segmentInfo, roadDetails, decoded);

    const updatedSegments = {
      ...state.selectedSegments,
      [segmentId]: newSegment
    };

    const segmentGroups = evaluateContiguity(updatedSegments);
    const selectedRoads = deriveSelectedRoads(segmentGroups);

    return {
      ...state,
      ...historyUpdate,
      selectedSegments: updatedSegments,
      segmentGroups,
      selectedRoads,
      isSegmentSelected: true,
      segmentError: false
    };
  }),

  on(ManagePostedSpeedActions.removeSegmentFromSelection, (state, { segmentId }) => {
    // If segment doesn't exist, return state unchanged
    if (!state.selectedSegments[segmentId]) {
      return state;
    }

    const historyUpdate = pushToHistory(state);

    const { [segmentId]: _removed, ...remaining } = state.selectedSegments;
    const hasSegments = Object.keys(remaining).length > 0;

    // If no segments remain, clear selection state
    if (!hasSegments) {
      return {
        ...state,
        ...historyUpdate,
        selectedSegments: {},
        segmentGroups: [],
        selectedRoads: [],
        isSegmentSelected: false,
        selectedSegmentId: null,
        selectedSegmentRef: null,
        decodedGeometry: null,
        segmentInfo: null
      };
    }

    const segmentGroups = evaluateContiguity(remaining);
    const selectedRoads = deriveSelectedRoads(segmentGroups);

    return {
      ...state,
      ...historyUpdate,
      selectedSegments: remaining,
      segmentGroups,
      selectedRoads,
      isSegmentSelected: true
    };
  }),

  on(ManagePostedSpeedActions.removeSegmentGroup, (state, { groupId }) => {
    // Find the group to remove
    const group = state.segmentGroups.find(g => g.groupId === groupId);
    if (!group) {
      return state;
    }

    const historyUpdate = pushToHistory(state);

    // Remove all segments belonging to this group
    const segmentIdsToRemove = new Set(group.segments.map(s => s.segmentId));
    const remaining = Object.keys(state.selectedSegments)
      .filter(id => !segmentIdsToRemove.has(id))
      .reduce(
        (acc, id) => {
          acc[id] = state.selectedSegments[id];
          return acc;
        },
        {} as typeof state.selectedSegments
      );

    const hasSegments = Object.keys(remaining).length > 0;

    // If no segments remain, clear selection state
    if (!hasSegments) {
      return {
        ...state,
        ...historyUpdate,
        selectedSegments: {},
        segmentGroups: [],
        selectedRoads: [],
        isSegmentSelected: false,
        selectedSegmentId: null,
        selectedSegmentRef: null,
        decodedGeometry: null,
        segmentInfo: null
      };
    }

    const segmentGroups = evaluateContiguity(remaining);
    const selectedRoads = deriveSelectedRoads(segmentGroups);

    return {
      ...state,
      ...historyUpdate,
      selectedSegments: remaining,
      segmentGroups,
      selectedRoads,
      isSegmentSelected: true
    };
  }),

  // Undo Segment Selection
  on(ManagePostedSpeedActions.undoSegmentSelection, (state): IManagePostedSpeedState => {
    if (state.past.length === 0) return state;

    const previousSnapshot = state.past[state.past.length - 1];
    const newPast = state.past.slice(0, -1);
    const newFuture = [...state.future, { selectedSegments: state.selectedSegments }];

    return restoreSnapshot(state, previousSnapshot, newPast, newFuture);
  }),

  // Redo Segment Selection
  on(ManagePostedSpeedActions.redoSegmentSelection, (state): IManagePostedSpeedState => {
    if (state.future.length === 0) return state;

    const nextSnapshot = state.future[state.future.length - 1];
    const newFuture = state.future.slice(0, -1);
    const newPast = [...state.past, { selectedSegments: state.selectedSegments }];

    return restoreSnapshot(state, nextSnapshot, newPast, newFuture);
  })
);

export function managePostedSpeedReducer(state: IManagePostedSpeedState | undefined, action: any): IManagePostedSpeedState {
  return reducer(state, action);
}
